class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    if (this.classList.contains('is-empty')) {
      this.querySelector('.cart-drawer__empty-content .button').addEventListener('click', this.close.bind(this));
    }
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    this._lastSyncAt = 0;

    this._onPageShow = (e) => {
      // iPhone BFcache fix
      if (e.persisted) {
        this.close();
        this.setHeaderCartIconAccessibility();
        this.syncCartDrawer('bfcache');
      }
    };
    window.addEventListener('pageshow', this._onPageShow);

    const schedule = window.requestIdleCallback || ((fn) => setTimeout(fn, 50));
    schedule(() => {
      this.setHeaderCartIconAccessibility();
      this.syncCartDrawer('product-load');
    });
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this._onPageShow);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    // Resetting listeners specifically for iOS Safari
    cartLink.onclick = (event) => {
      event.preventDefault();
      this.open(cartLink);
    };

    cartLink.onkeydown = (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    };
  }

  async syncCartDrawer(reason = 'manual') {
    try {
      const res = await fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble&ts=${Date.now()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();

      const parser = new DOMParser();

      let isEmpty = false;

      if (json['cart-drawer']) {
        const drawerHTML = parser.parseFromString(json['cart-drawer'], 'text/html');
        const incomingInner = drawerHTML.querySelector('.drawer__inner');
        const currentInner = this.querySelector('.drawer__inner');

        if (incomingInner && currentInner) {
          currentInner.innerHTML = incomingInner.innerHTML;
        }

        isEmpty = drawerHTML.querySelector('.drawer__inner--empty, .cart-drawer__empty-content') !== null;
        this.classList.toggle('is-empty', isEmpty);
      }

      if (json['cart-icon-bubble']) {
        const bubbleHTML = parser.parseFromString(json['cart-icon-bubble'], 'text/html');
        const incomingCount = bubbleHTML.querySelector('.cart-count-bubble');
        const currentBubbleContainer = document.querySelector('#cart-icon-bubble');

        if (currentBubbleContainer) {
          const currentCount = currentBubbleContainer.querySelector('.cart-count-bubble');

          if (incomingCount) {
            if (currentCount) {
              currentCount.innerHTML = incomingCount.innerHTML;
            } else {
              currentBubbleContainer.appendChild(incomingCount);
            }
          } else if (currentCount) {
            currentCount.remove();
          }
        }

        // Update the subtotal (The "Cart Total")
        const incomingTotal = bubbleHTML.querySelector('.cart-total');
        const currentTotal = document.querySelector('.cart-total');
        if (incomingTotal && currentTotal) {
          currentTotal.innerHTML = incomingTotal.innerHTML;
        }
      }

      this._lastSyncAt = Date.now();
      console.debug('[cart] drawer refreshed:', reason, 'empty:', isEmpty);
    } catch (err) {
      console.warn('[cart] drawer refresh failed:', err);
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    // If we haven’t synced in 3s, do a quick sync first.
    const now = Date.now();
    if (!this._lastSyncAt || now - this._lastSyncAt > 3000) {
      // Fire and forget; the transition can still start
      this.syncCartDrawer('pre-open').catch(() => {});
    }

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    // reliable transition
    this.classList.add('animate');
    this.offsetWidth; // reflow
    requestAnimationFrame(() => this.classList.add('active'));

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (sectionElement && this.getSectionInnerHTML(parsedState.sections[section.id], section.selector)) {
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.classList.remove('is-empty');
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
